<template>
    <!-- One markup, two elements: the top bar's is a control and the drawer header's
         is a picture of the same thing. A decorative copy must not be a second tab
         stop for the panel it is already inside the heading of. -->
    <component
        :is="decorative ? 'span' : 'button'"
        :type="decorative ? undefined : 'button'"
        class="relative inline-flex shrink-0 items-center justify-center rounded-full p-1.5"
        :class="decorative
            ? ''
            : 'transition-colors hover:bg-elevated focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'"
        :aria-label="decorative ? undefined : label"
        :aria-hidden="decorative ? 'true' : undefined"
        :title="decorative ? undefined : label"
        @click="decorative ? undefined : emit('click')"
    >
        <!-- Since v1.450.0 the picture inside the control is Milo's 3D head
             (MiloHead.vue), which passes it in here and keeps the button, the label
             and the dot below. The sphere is the DEFAULT content, so this component
             standing alone is exactly what it always was — and is what gets drawn
             where the head cannot be. -->
        <slot>
            <span class="assistant-orb" :class="`is-${state}`" :style="{ width: `${size}px`, height: `${size}px` }">
                <!-- The moving light. One element, so the whole animation is a rotation of
                     a single layer rather than a keyframe per highlight. -->
                <span class="assistant-orb-glow" />
                <!-- The specular highlight: the thing that makes a flat circle read as a
                     sphere. It does not move, because a light source that follows the
                     object is what makes CG look like CG. -->
                <span class="assistant-orb-spec" />
            </span>
        </slot>

        <span v-if="state === 'attention' && !decorative" class="assistant-orb-dot" />
    </component>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    import type { AssistantOrbState } from '@/libs/assistant'

    /**
     * The assistant, as one object in the top bar.
     *
     * # Two jobs since v1.450.0
     *
     * The CONTROL — the button, the accessible name, the attention dot — and the
     * picture inside it, which is a CSS sphere. Milo's 3D head (`MiloHead.vue`) draws
     * this component with the picture replaced through the default slot, so the button
     * and its label are written once and the head is only ever pixels. When the head
     * cannot be drawn — no WebGL, or the package failed to load — nothing fills the
     * slot and the sphere below is what the desk sees. It is the fallback, so it stays.
     *
     * # Why an orb and not an icon
     *
     * It replaces a menu entry, and a menu entry is a place you go; this is a thing you
     * talk to, sitting beside the bell for the whole shift. It has to say three things
     * without being read — that it is there, that it is working, that something is
     * waiting — and a flat icon can only say the last of those with a badge.
     *
     * # How it is drawn
     *
     * CSS and nothing else: no WebGL, no Lottie, no dependency. A sphere is three
     * layers over one colour —
     *
     *   1. the body, `--ui-primary` with a radial terminator shading it towards the
     *      bottom right, so the ball has a lit side and a dark side;
     *   2. a rotating conic glow inside it, which is the only part that ever moves in
     *      `thinking` — light travelling under a surface rather than the surface
     *      spinning, because a spinning ball has no reason to be spinning;
     *   3. a fixed specular highlight at the upper left, plus a soft contact shadow
     *      underneath.
     *
     * The colour is `--ui-primary` and never a raw palette step, so the orb is the
     * house's own colour in both themes and follows a rebrand (deviation 22 is about
     * raw ramp CLASSES and this adds none). The light and the shade over it are the
     * `--orb-*` tokens defined in `assets/main.css` — white and black at alpha, which
     * is what light and shadow ARE and why they read correctly over any primary. No
     * colour value is spelled in this file.
     *
     * # States
     *
     *   idle      — a slow four-second breath. Present, not asking for anything.
     *   thinking  — the inner glow turns while a request is in flight.
     *   attention — a proposal is waiting for Potrdi, or an answer has not been read.
     *               A dot on the rim, ringed in the page's own background so it reads
     *               against an orb of the same colour.
     *   (off)     — not this component's business: the top bar does not render it at
     *               all where the account has no `chat:use`.
     *
     * `prefers-reduced-motion` stops every animation. The states stay distinguishable
     * without motion — the glow is still there, the dot is still there — which is the
     * test that decides whether motion was carrying meaning it should not have been.
     */

    const props = withDefaults(defineProps<{
        state?: AssistantOrbState
        /** Edge length in pixels. 36 in the top bar, smaller in the drawer's header. */
        size?:  number
        /** A picture of the orb rather than the control — the drawer's own header,
         *  which is already inside the panel the control opens. */
        decorative?: boolean
    }>(), {
        state:      'idle',
        size:       36,
        decorative: false,
    })

    const emit = defineEmits<{ click: [] }>()

    const { t } = useI18n()

    /**
     * What a screen reader is told, and what the tooltip says.
     *
     * The name comes first in every state, because the shortcut and the state are
     * qualifiers of a thing that has to be identifiable without them.
     */
    const label = computed((): string => {
        const name = `${t('assistant.title')} (${t('assistant.shortcut')})`

        if (props.state === 'thinking')  return `${name} — ${t('assistant.thinking')}`
        if (props.state === 'attention') return `${name} — ${t('assistant.orb_attention')}`

        return name
    })
</script>

<style scoped>
    .assistant-orb {
        position: relative;
        display: block;
        border-radius: 9999px;
        background-color: var(--ui-primary);

        /* The terminator: transparent over the lit third, deepening to a shaded rim.
         * Black rather than a darker step of the ramp, so it is shade over whatever
         * colour the house has chosen and not a second colour to keep in step. */
        background-image: radial-gradient(circle at 34% 30%,
            var(--orb-lit) 0%,
            var(--orb-clear) 38%,
            var(--orb-shade) 62%,
            var(--orb-shade-deep) 100%);

        /* Inside: the rim light along the bottom edge. Outside: the contact shadow
         * that lifts it off the bar. */
        box-shadow:
            inset 0 -1px 3px var(--orb-rim-dark),
            inset 0 1px 2px var(--orb-rim-light),
            0 1px 3px var(--orb-contact);

        animation: assistant-orb-breathe 4s ease-in-out infinite;
    }

    .assistant-orb-glow,
    .assistant-orb-spec {
        position: absolute;
        border-radius: 9999px;
        pointer-events: none;
    }

    /* Light under the surface. Invisible until it has something to say. */
    .assistant-orb-glow {
        inset: 12%;
        opacity: 0;
        background-image: conic-gradient(from 0deg,
            var(--orb-clear) 0deg,
            var(--orb-glow) 90deg,
            var(--orb-clear) 200deg,
            var(--orb-clear) 360deg);
        filter: blur(2px);
        transition: opacity 200ms ease;
    }

    .assistant-orb-spec {
        top: 12%;
        left: 16%;
        width: 34%;
        height: 26%;
        background-image: radial-gradient(ellipse at center,
            var(--orb-spec) 0%,
            var(--orb-spec-soft) 45%,
            var(--orb-clear) 72%);
    }

    .assistant-orb.is-thinking {
        animation: none;
    }

    .assistant-orb.is-thinking .assistant-orb-glow {
        opacity: 1;
        animation: assistant-orb-spin 1.4s linear infinite;
    }

    /* Waiting is a slightly faster, slightly deeper breath — enough to catch the eye
     * of somebody who is not looking at the top bar, and not enough to be a flash. */
    .assistant-orb.is-attention {
        animation: assistant-orb-waiting 2s ease-in-out infinite;
    }

    .assistant-orb-dot {
        position: absolute;
        top: 0.25rem;
        right: 0.25rem;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 9999px;
        background-color: var(--ui-primary);
        /* Ringed in the bar's own background: a primary dot on a primary sphere is
         * one shape, and the ring is what makes it two. */
        box-shadow: 0 0 0 2px var(--ui-bg);
    }

    @keyframes assistant-orb-breathe {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.05); }
    }

    @keyframes assistant-orb-waiting {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.09); }
    }

    @keyframes assistant-orb-spin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
    }

    /* Nothing moves for somebody who has asked for nothing to move. The states are
     * still told apart by the glow and the dot, which is the point of drawing them. */
    @media (prefers-reduced-motion: reduce) {
        .assistant-orb,
        .assistant-orb.is-attention,
        .assistant-orb.is-thinking .assistant-orb-glow {
            animation: none;
        }
    }
</style>
